import clsx from 'clsx';
import React from 'react';
import { Avatar, Breadcrumbs, Button } from 'ui';
import type { BreadcrumbItem } from 'ui';
import './_teacher-page-header.scss';

/**
 * TeacherPageHeader - Shared header strip for the teacher AI panel screens, rendered as the
 * first child of the TeacherLayoutWrapper main region. Three mutually exclusive shapes:
 * a page title (optionally with a study-buddy illustration and an actions slot), a
 * breadcrumb trail (optionally with a description), or a temporary back navigation with an
 * optional student chip. Presentational and pure; copy comes from each view.
 */

export type TeacherPageHeaderStudent = {
  avatar: string | null;
  displayName: string;
};

type TitleHeaderProps = {
  actions?: React.ReactNode;
  robot?: 'help' | 'search';
  subtitle?: string;
  title: string;
  variant: 'title';
};

type BreadcrumbsHeaderProps = {
  description?: string;
  items: BreadcrumbItem[];
  variant: 'breadcrumbs';
};

type BackHeaderProps = {
  backLabel: string;
  onBack: () => void;
  student?: TeacherPageHeaderStudent;
  variant: 'back';
};

export type TeacherPageHeaderProps = TitleHeaderProps | BreadcrumbsHeaderProps | BackHeaderProps;

function TitleHeader({ actions, robot, subtitle, title }: Readonly<TitleHeaderProps>): JSX.Element {
  return (
    <header className={clsx('teacher-page-header', 'teacher-page-header--title', robot && 'teacher-page-header--illustrated')}>
      <div className="teacher-page-header__block">
        <h1 className="teacher-page-header__title">{title}</h1>
        {robot && (
          <div className="teacher-page-header__robot-row">
            <span aria-hidden="true" className={clsx('teacher-page-header__robot', `teacher-page-header__robot--${robot}`)} />
            {subtitle && <p className="teacher-page-header__subtitle">{subtitle}</p>}
          </div>
        )}
        {!robot && subtitle && <p className="teacher-page-header__subtitle">{subtitle}</p>}
      </div>
      {actions && <div className="teacher-page-header__actions">{actions}</div>}
    </header>
  );
}

function BreadcrumbsHeader({ description, items }: Readonly<BreadcrumbsHeaderProps>): JSX.Element {
  return (
    <header className="teacher-page-header teacher-page-header--breadcrumbs">
      <div className="teacher-page-header__block">
        <Breadcrumbs className="teacher-page-header__breadcrumbs" items={items} />
        {description && <p className="teacher-page-header__description">{description}</p>}
      </div>
    </header>
  );
}

function BackHeader({ backLabel, onBack, student }: Readonly<BackHeaderProps>): JSX.Element {
  return (
    <header className="teacher-page-header teacher-page-header--back">
      <div className="teacher-page-header__block">
        <div className="teacher-page-header__back">
          <Button leftIcon="arrow-left" onClick={onBack} text={backLabel} variant="plain-primary" />
        </div>
        {student && (
          <div className="teacher-page-header__student">
            <Avatar image={student.avatar ?? undefined} size="sm" />
            <h1 className="teacher-page-header__student-name">{student.displayName}</h1>
          </div>
        )}
      </div>
    </header>
  );
}

function TeacherPageHeader(props: Readonly<TeacherPageHeaderProps>): JSX.Element {
  switch (props.variant) {
    case 'title':
      return <TitleHeader {...props} />;
    case 'breadcrumbs':
      return <BreadcrumbsHeader {...props} />;
    case 'back':
      return <BackHeader {...props} />;
    default: {
      const exhaustiveCheck: never = props;
      return exhaustiveCheck;
    }
  }
}

export default TeacherPageHeader;
